import { MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import { type Response } from 'core/api'
import { useEffect, useState } from 'react'
import { NotificationManager } from 'react-notifications'
import { useInitialEffect } from '../../core/react-utils'
import { organizationUrl } from '../../core/utils'
import JiraIssueForm from '../../models/forms/jira-issue-form'
import { type ReviewStatus, ReviewType } from '../../models/review-data'
import { AuthorizationProvider } from '../../models/settings'
import { type TestRunTest } from '../../models/test-run'
import { ModalStore, ReviewStore, SettingsStore, TestRunStore } from '../../stores'

interface Props {
  reviewType: ReviewType
  testRunId: number
  testRunReviewStatusId: number | null
  testRunTestId: number
  testRunTestReviewStatusId: number | undefined
  refresh?: (testRunId: number) => any
  testRunTest?: TestRunTest | null
}

const ReviewStatusSelect = ({
  reviewType,
  testRunId,
  testRunReviewStatusId,
  testRunTestId,
  testRunTestReviewStatusId,
  refresh,
  testRunTest: testRunTestProp
}: Props): JSX.Element => {
  const [value, setValue] = useState<string>('')
  const [testRunTest, setTestRunTest] = useState<TestRunTest | null>(null)
  const [currentTestRunTestReviewStatusId, setCurrentTestRunTestReviewStatusId] = useState<number | null>(null)
  const [reviewStatuses, setReviewStatuses] = useState<ReviewStatus[]>([])

  const options = reviewStatuses.map(status => {
    return <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
  })

  useInitialEffect(async () => {
    if (testRunTestProp != null) {
      setTestRunTest(testRunTestProp)
    } else {
      setTestRunTest(await TestRunStore.loadTestRunTest(testRunTestId) as TestRunTest | null)
    }
    setReviewStatuses(await ReviewStore.loadReviewStatuses())
    setCurrentTestRunTestReviewStatusId(testRunTestReviewStatusId ?? null)
  })

  useEffect(() => {
    let currentStatusId: number | null
    switch (reviewType) {
      case ReviewType.TEST_RUN:
        currentStatusId = testRunReviewStatusId
        break
      case ReviewType.TEST_RUN_TEST:
        currentStatusId = testRunTestReviewStatusId ?? null
        break
    }

    const value = reviewStatuses.find(status => status.id === currentStatusId)?.id
    setValue(value == null ? '' : value.toString())
  }, [reviewStatuses, reviewType, testRunReviewStatusId, testRunTestReviewStatusId])

  async function handleChange (e: SelectChangeEvent<string>): Promise<void> {
    const reviewStatusId = e.target.value

    const numericStatusId = reviewStatusId !== '' ? Number(reviewStatusId) : null

    if (reviewType === ReviewType.TEST_RUN) {
      if (numericStatusId === testRunReviewStatusId) { return }

      testRunReviewStatusId = numericStatusId
    } else {
      if (numericStatusId === currentTestRunTestReviewStatusId) { return }

      setCurrentTestRunTestReviewStatusId(numericStatusId)
    }

    setValue(reviewStatusId)

    if (testRunTest === null) return

    const response: Response<any> | undefined = await ReviewStore.updateReviewStatus({
      reviewType,
      testRunId,
      testRunTestId: testRunTest.id,
      reviewStatusId: numericStatusId ?? undefined
    })
      .catch(() => NotificationManager.error('Please try again', 'Something went wrong'))

    let message
    if (reviewType === ReviewType.TEST_RUN) { message = 'Test run review status successfully updated' } else { message = 'Test review status successfully updated' }

    if (response != null && response.status === 200) { NotificationManager.success(message, 'Success', 2000) }

    if (reviewType === ReviewType.TEST_RUN_TEST && SettingsStore.authorizations.find(authorization => authorization.provider === AuthorizationProvider.Jira) != null) {
      const jiraIssueForm = new JiraIssueForm()
      jiraIssueForm.updateField('testRunId', [testRunId])
      jiraIssueForm.updateField('testRunTestIds', [testRunTest.id])
      jiraIssueForm.updateField('summary', `Fix "${testRunTest.projectTest.name}" Test`)
      jiraIssueForm.updateField('rootCauseId', testRunTest.testRunAnalysisRootCauseId)

      const description = `Test: ${window.location.origin + organizationUrl(`test-runs/${testRunId}/tests/${testRunTest.id}`)}`
      jiraIssueForm.updateField('description', description)

      ModalStore.confirmation.show(() => {
      }, {
        message: 'Would you like to create a Jira Issue',
        okText: 'Yes',
        cancelText: 'No',
        okClick: () => {
          ModalStore.createJiraModal.show(() => {
            if (refresh != null) { refresh(testRunId) }
          }, jiraIssueForm
          )
        }
      })
    }
  }

  return (
    <>
      <Select style={{ background: '#FFF', textAlign: 'left', width: '200px' }}
              displayEmpty
              value={value}
              onChange={(e) => { void handleChange(e) }}>
        <MenuItem key={''} value={''}><em>Review Status (None)</em></MenuItem>
        {options}
      </Select>
    </>
  )
}

export default ReviewStatusSelect
